import { normalize, schema } from 'normalizr';
import { createLogic } from 'redux-logic';
import { stateGo } from 'redux-ui-router';
import {
  camelCase,
  filter,
  find,
  findKey,
  get,
  has,
  includes,
  intersection,
  map,
  mapValues,
  pick,
  pull,
  reverse,
  sortBy,
} from 'lodash';

import { hosts } from 'settings';
import { authLogout } from './auth';
import { decorateMember } from 'decorators';

import {
  PROFILE_NUTRITION,
  PROFILE_WORKOUT,
  SPECIAL_REQUIREMENT_PRODUCTS,
} from '../constants';

import {
  calculateDaysElapsed,
  formatDateString,
  memberStorage,
  stateParamsForSubscription,
} from 'utils';

// Action Types
const FETCH = '@@app/member/FETCH';
const FETCH_REDIRECT = '@@app/member/FETCH_REDIRECT';
const FETCH_SUCCESS = '@@app/member/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/member/FETCH_ERROR';
const FETCH_INVOICES = '@@app/member/FETCH_INVOICES';
const FETCH_INVOICES_SUCCESS = '@@app/member/FETCH_INVOICES_SUCCESS';

const UPDATE = '@@app/member/UPDATE';
const UPDATE_SUCCESS = '@@app/member/UPDATE_SUCCESS';
const UPDATE_ERROR = '@@app/member/UPDATE_ERROR';
const UPDATE_FIELD = '@@app/member/UPDATE_FIELD';
const UPDATE_PASSWORD = '@@app/member/UPDATE_PASSWORD';
const UPDATE_SUBSCRIPTION = '@@app/member/UPDATE_SUBSCRIPTION';

const START_SUBSCRIPTION = '@@app/member/START_SUBSCRIPTION';
const RESTART_SUBSCRIPTION = '@@app/member/RESTART_SUBSCRIPTION';
const CHANGE_SUBSCRIPTION = '@@app/member/CHANGE_SUBSCRIPTION';
const RELOAD_PROGRAM = '@@app/member/RELOAD_PROGRAM';

const RESET_PASSWORD_INIT = '@@app/member/RESET_PASSWORD_INIT';
const RESET_PASSWORD_CREATE = '@@app/member/RESET_PASSWORD_CREATE';
const RESET_PASSWORD_UPDATE = '@@app/member/RESET_PASSWORD_UPDATE';
const RESET_PASSWORD_SUCCESS = '@@app/member/RESET_PASSWORD_SUCCESS';
const RESET_PASSWORD_ERROR = '@@app/member/RESET_PASSWORD_ERROR';

const DOWNGRADE_SUBSCRIPTION = '@@app/member/DOWNGRADE_SUBSCRIPTION';
const UPGRADE_SUBSCRIPTION = '@@app/member/UPGRADE_SUBSCRIPTION';
const MIGRATE_SUBSCRIPTION_ERROR = '@@app/member/MIGRATE_SUBSCRIPTION_ERROR';
const MIGRATE_SUBSCRIPTION_SUCCESS =
  '@@app/member/MIGRATE_SUBSCRIPTION_SUCCESS';

const CANCEL_SUBSCRIPTION = '@@app/member/CANCEL_SUBSCRIPTION';
const CANCEL_SUBSCRIPTION_ERROR = '@@app/member/CANCEL_SUBSCRIPTION_ERROR';
const RESUME_SUBSCRIPTION = '@@app/member/RESUME_SUBSCRIPTION';

const CANCEL = '@@app/member/CANCEL';

// Initial State
export const initialState = {
  isLoading: false,
  error: false,
  success: false,
  message: '',
  entities: {
    invoices: [],
    members: {},
    subscriptions: {},
  },
  result: null,
};

// Observables
const fetchLogic = createLogic({
  type: FETCH,
  debounce: process.env.NODE_ENV === 'test' ? 0 : 500,
  latest: true,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient, memberStorage }) {
    return httpClient
      .get(`/v1/voome/members/${action.payload.memberId}`)
      .then(response => {
        const subscription = new schema.Entity('subscriptions');
        const member = new schema.Entity('members', {
          subscriptions: [subscription],
        });

        return normalize(decorateMember(response.data), member);
      })
      .catch(() => ({
        message: 'An error occured fetching the member',
      }));
  },
});

const fetchInvoicesLogic = createLogic({
  type: FETCH_INVOICES,
  processOptions: {
    successType: FETCH_INVOICES_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    return httpClient.get(
      `/v1/voome/members/${action.payload.memberId}/invoices`
    );
  },
});

const resetPasswordCreateLogic = createLogic({
  type: RESET_PASSWORD_CREATE,
  processOptions: {
    successType: RESET_PASSWORD_SUCCESS,
    failType: RESET_PASSWORD_ERROR,
  },
  process({ action, httpClient }) {
    const { email, theme, callbackUrl } = action.payload;
    const member = { email };

    const onSuccess = () => {
      const message = `An email has been sent to ${email}. Follow the
        directions within the email to reset your password.`;
      return { message };
    };

    return httpClient
      .post('/v1/password', { member, callbackUrl, theme })
      .then(onSuccess);
  },
});

const resetPasswordUpdateLogic = createLogic({
  type: RESET_PASSWORD_UPDATE,
  process({ action, httpClient }, dispatch, done) {
    const member = pick(action.payload, [
      'email',
      'password',
      'resetPasswordToken',
    ]);

    const onSuccess = () => {
      const message = `The password for ${member.email} has been updated.
        You can now login with your new password`;

      dispatch({ type: RESET_PASSWORD_SUCCESS, payload: { message } });
    };

    const onFail = ({ response }) => {
      const error = get(response, 'data.error.reset_password_token[0]');
      const message =
        error === 'is invalid'
          ? 'Reset password token is no longer valid'
          : 'An error occured updating your password';

      dispatch({
        type: RESET_PASSWORD_ERROR,
        payload: {
          message: `${message}, try submitting your email again.`,
        },
      });

      dispatch(
        stateGo('resetPassword', {
          reset_password_token: undefined,
        })
      );
    };

    return httpClient
      .put('/v1/password', { member })
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

const updateMemberLogic = createLogic({
  type: UPDATE,
  cancelType: CANCEL,
  process({ action, getState, httpClient }, dispatch, done) {
    const memberId = get(action.payload.member, 'id');
    const member = pick(action.payload.member, [
      'birthday',
      'country',
      'email',
      'firstName',
      'gender',
      'lastName',
      'password',
      'postcode',
      'profile',
      'voomeProfile',
      'screenName',
      'startWeightInKg',
    ]);

    const onSuccess = () => {
      const { step } = action.payload;

      if (step) {
        const { id } = selectSubscription(getState().member);
        const subscription = { id, step };

        dispatch({
          type: UPDATE_SUBSCRIPTION,
          payload: { subscription, memberId },
        });
      } else {
        dispatch(fetchMember(memberId));
      }
    };

    const onFail = () => {
      dispatch({
        type: UPDATE_ERROR,
        error: true,
        payload: {
          message: 'An error occured updating. Please try again.',
        },
      });
    };

    httpClient
      .put(`/v3/members/${memberId}`, { member })
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

const updateSubscriptionLogic = createLogic({
  type: [UPDATE_SUBSCRIPTION, START_SUBSCRIPTION, RESTART_SUBSCRIPTION],
  process({ action, httpClient }, dispatch, done) {
    const { subscription, memberId } = action.payload;
    const { startDate, week, voomeProductId } = subscription;

    const onSuccess = () => {
      switch (action.type) {
        case START_SUBSCRIPTION:
          dispatch({
            type: RELOAD_PROGRAM,
            payload: {
              voomeProductId,
              weekNumber: startDate === formatDateString(new Date()) ? 1 : 0,
            },
          });
          break;

        case RESTART_SUBSCRIPTION:
          dispatch({
            type: RELOAD_PROGRAM,
            payload: {
              voomeProductId,
              weekNumber: week,
            },
          });
          break;

        default:
          dispatch(fetchMember(memberId));
          break;
      }
    };

    const onFail = () => {
      dispatch({
        type: UPDATE_ERROR,
        error: true,
        payload: {
          message: 'An error occured updating. Please try again.',
        },
      });
    };

    let params = { subscription };
    let endpoint = `/v1/voome/subscriptions/${subscription.id}`;

    switch (action.type) {
      case START_SUBSCRIPTION:
        params = { startDate };
        endpoint += '/start';
        break;

      case RESTART_SUBSCRIPTION:
        params = { week };
        endpoint += '/restart';
        break;
    }

    return httpClient
      .put(endpoint, params)
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

const cancelSubscriptionLogic = createLogic({
  type: CANCEL_SUBSCRIPTION,
  process({ action, httpClient }, dispatch, done) {
    const { memberId, subscriptionId, params } = action.payload;
    const endpoint = `/v1/voome/subscriptions/${subscriptionId}/delayed-cancel`;
    const onSuccess = () => dispatch(fetchMember(memberId));
    const onFail = () => dispatch({ type: CANCEL_SUBSCRIPTION_ERROR });

    return httpClient
      .put(endpoint, params)
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

const migrateSubscriptionLogic = createLogic({
  type: [DOWNGRADE_SUBSCRIPTION, UPGRADE_SUBSCRIPTION],
  processOptions: {
    successType: MIGRATE_SUBSCRIPTION_SUCCESS,
    failType: MIGRATE_SUBSCRIPTION_ERROR,
  },
  process({ action, dataLayer, httpClient }) {
    const { step, subscriptionId, voomeProductId } = action.payload;
    const endpoint = `/v1/voome/subscriptions/${subscriptionId}/migrate`;
    const type =
      action.type === DOWNGRADE_SUBSCRIPTION ? 'downgrade' : 'upgrade';

    const onSuccess = response => {
      memberStorage.setKey('voomeProductId', voomeProductId);
      dataLayer.track(type, response.data);
      return response.data;
    };

    return httpClient.put(endpoint, { voomeProductId, step }).then(onSuccess);
  },
});

const resumeSubscriptionLogic = createLogic({
  type: RESUME_SUBSCRIPTION,
  process({ action, httpClient }, dispatch, done) {
    const { memberId, subscriptionId } = action.payload;
    const endpoint = `/v1/voome/subscriptions/${subscriptionId}/resume`;
    const onSuccess = () => dispatch(fetchMember(memberId));

    return httpClient
      .put(endpoint)
      .then(onSuccess)
      .then(done);
  },
});

const reloadProgramLogic = createLogic({
  type: RELOAD_PROGRAM,
  process({ action, memberStorage }, dispatch, done) {
    const { voomeProductId, weekNumber } = action.payload;

    let name = 'program';
    let params = { voomeProductId };
    let uri = `/program/${voomeProductId}`;

    if (weekNumber > 0) {
      name = 'program.week.detail';
      params.weekNumber = weekNumber;
      uri = `/program/${voomeProductId}/week/${weekNumber}`;
    }

    memberStorage.setKey('redirectTo', { name, params });
    window.location.assign(uri);
    done();
  },
});

const changeSubscriptionLogic = createLogic({
  type: CHANGE_SUBSCRIPTION,
  process({ action, memberStorage }, dispatch, done) {
    const { subscription } = action.payload;
    const { name, params } = stateParamsForSubscription(subscription);
    memberStorage.setKey('voomeProductId', subscription.voomeProductId);
    dispatch(stateGo(name, params));
    done();
  },
});

const updatePasswordLogic = createLogic({
  type: UPDATE_PASSWORD,
  process({ action, httpClient }, dispatch, done) {
    const { memberId, params } = action.payload;
    const member = pick(params, [
      'currentPassword',
      'password',
      'passwordConfirmation',
    ]);

    const onSuccess = () => {
      // Not great.. but having issues handling the modal form.
      window.location.assign('/profile');
    };

    const onFail = ({ response }) => {
      const error = get(response, 'data.errors.current_password[0]');
      const message =
        error === 'is invalid' ? 'Incorrect password' : 'An error occured';

      dispatch({
        type: UPDATE_ERROR,
        payload: {
          message: `${message}, please try again.`,
        },
      });
    };

    return httpClient
      .put(`v1/members/${memberId}/update-password`, { member })
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

export const memberLogic = [
  changeSubscriptionLogic,
  fetchInvoicesLogic,
  fetchLogic,
  cancelSubscriptionLogic,
  migrateSubscriptionLogic,
  reloadProgramLogic,
  resetPasswordCreateLogic,
  resetPasswordUpdateLogic,
  resumeSubscriptionLogic,
  updateMemberLogic,
  updatePasswordLogic,
  updateSubscriptionLogic,
];

// Action Creators
export const fetchMember = memberId => {
  return { type: FETCH, payload: { memberId } };
};

export const fetchInvoices = (memberId, voomeProductId) => {
  return { type: FETCH_INVOICES, payload: { memberId, voomeProductId } };
};

export const updateMember = member => {
  return { type: UPDATE, payload: { member } };
};

export const updateMemberStep = (member, step = 1) => {
  return { type: UPDATE, payload: { member, step } };
};

export const updateMemberField = (key, value) => {
  return { type: UPDATE_FIELD, payload: { key, value } };
};

export const updateMemberPassword = (memberId, params) => {
  return { type: UPDATE_PASSWORD, payload: { memberId, params } };
};

export const downgradeSubscription = (subscriptionId, voomeProductId) => {
  return {
    type: DOWNGRADE_SUBSCRIPTION,
    payload: { subscriptionId, voomeProductId },
  };
};

export const upgradeSubscription = (subscriptionId, voomeProductId) => {
  return {
    type: UPGRADE_SUBSCRIPTION,
    payload: { step: 2, subscriptionId, voomeProductId },
  };
};

export const startSubscription = subscription => {
  return { type: START_SUBSCRIPTION, payload: { subscription } };
};

export const restartSubscription = subscription => {
  return { type: RESTART_SUBSCRIPTION, payload: { subscription } };
};

export const cancelSubscription = (memberId, subscriptionId, params) => {
  return {
    type: CANCEL_SUBSCRIPTION,
    payload: { memberId, subscriptionId, params },
  };
};

export const resumeSubscription = (memberId, subscriptionId) => {
  return {
    type: RESUME_SUBSCRIPTION,
    payload: { memberId, subscriptionId },
  };
};

export const changeSubscription = subscription => {
  return { type: CHANGE_SUBSCRIPTION, payload: { subscription } };
};

export const resetPasswordInit = () => {
  return { type: RESET_PASSWORD_INIT };
};

export const resetPasswordCreate = payload => {
  return { type: RESET_PASSWORD_CREATE, payload };
};

export const resetPasswordUpdate = payload => {
  return { type: RESET_PASSWORD_UPDATE, payload };
};

// Selectors
export const selectMember = state => {
  return state.entities.members[state.result];
};

export const selectInvoices = state => {
  return state.entities.invoices;
};

export const selectMemberLevel = state => {
  return get(selectMember(state), 'voomeProfile.level', 0);
};

export const selectMemberIsVegetarian = state => {
  return get(selectMember(state), 'profile.vegetarian', false);
};

export const selectSubscriptions = state => {
  const invoices = selectInvoices(state);

  return mapValues(state.entities.subscriptions, subscription => {
    return {
      ...subscription,
      invoices: filter(invoices, {
        billableId: subscription.id,
        billableType: 'Voome::Subscription',
      }),
    };
  });
};

export const selectSubscription = (state, voomeProductId) => {
  return (
    find(selectSubscriptions(state), {
      voomeProductId: parseInt(
        voomeProductId || memberStorage.get().voomeProductId,
        10
      ),
    }) || {}
  );
};

export const selectSubscriptionWithFallback = state => {
  const subscription = selectSubscription(state);

  return subscription.status === 'active'
    ? subscription
    : selectSubscriptionFallback(state);
};

export const selectSubscriptionFallback = state => {
  const subscriptions = reverse(
    sortBy(selectSubscriptions(state), 'startedAt')
  );

  return subscriptions.length > 0
    ? find(subscriptions, { status: 'active' }) || subscriptions[0]
    : {};
};

export const selectSubscriptionHasStarted = (state, voomeProductId) => {
  const subscription = selectSubscription(state, voomeProductId) || {};
  return subscription.startedAt ? true : false;
};

export const selectHasNutritionSubscription = state => {
  return (
    selectHasBundleSubscription(state) ||
    selectHasSubscriptionWithSlug(state, 'voome-nutrition')
  );
};

export const selectHasWorkoutSubscription = state => {
  return (
    selectHasBundleSubscription(state) ||
    selectHasSubscriptionWithSlug(state, 'voome-workouts')
  );
};

export const selectHasBundleSubscription = state => {
  return selectHasSubscriptionWithSlug(state, 'voome-bundle');
};

export const selectHasSubscriptionWithSlug = (state, slug) => {
  return !!findKey(selectSubscriptions(state), item => item.slug === slug);
};

export const selectHasWeeklySubscription = state => {
  return !!findKey(selectSubscriptions(state), item => item.isWeekly);
};

export const selectHasProgramSubscription = state => {
  return !!findKey(selectSubscriptions(state), item => !item.isWeekly);
};

export const selectIsMatched = (state, tags) => {
  const { preferenceNames } = selectMember(state).voomeProfile;
  return intersection(tags, preferenceNames).length >= 3;
};

export const selectNutritionQuestions = state => {
  const member = selectMember(state);

  return member.voomeProfile
    ? PROFILE_NUTRITION.map(item =>
        mapAnswerToQuestion(item, member.voomeProfile.preferences)
      )
    : [];
};

export const selectWorkoutQuestions = state => {
  const member = selectMember(state);

  return member.voomeProfile
    ? PROFILE_WORKOUT.map(item =>
        mapAnswerToQuestion(item, member.voomeProfile.preferences)
      )
    : [];
};

export const selectPrintTooltip = state => {
  const { canPrint, printsRemained, isTrial } = selectSubscription(state);

  return (
    canPrint &&
    isTrial &&
    `You can print ${printsRemained} more times in your free trial week.`
  );
};

export const selectIsProductLowFodmap = (state, voomeProductId) => {
  const subscription = selectSubscription(state, voomeProductId) || {};

  return (
    has(subscription.productDietaryPreference, 'lowFODMAP') &&
    subscription.productDietaryPreference.lowFODMAP
  );
};

export const selectIsProductWithRequirements = (state, voomeProductId) => {
  const subscription = selectSubscription(state, voomeProductId) || {};
  const dietaryPreference = filter(
    subscription.productDietaryPreference,
    p => p === true
  );

  return (
    (dietaryPreference && dietaryPreference.length > 0) ||
    includes(SPECIAL_REQUIREMENT_PRODUCTS, subscription.slug)
  );
};

// Helpers
const mapAnswerToQuestion = (item, preferences) => {
  const answers = map(filter(preferences[item.key], 'active'), 'name');

  return {
    ...item,
    answer: answers.length ? answers.join(', ') : 'Not Set',
  };
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH:
    case UPDATE:
    case RESET_PASSWORD_INIT:
    case RESET_PASSWORD_CREATE:
    case RESET_PASSWORD_UPDATE:
    case DOWNGRADE_SUBSCRIPTION:
    case UPGRADE_SUBSCRIPTION:
    case CANCEL_SUBSCRIPTION:
    case RESUME_SUBSCRIPTION:
      return {
        ...state,
        error: false,
        success: false,
        isLoading: true,
        message: '',
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: false,
        success: true,
        result: payload.result,
        entities: {
          ...state.entities,
          ...payload.entities,
        },
      };

    case FETCH_INVOICES_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: false,
        success: true,
        entities: {
          ...state.entities,
          invoices: payload.data,
        },
      };

    case FETCH_ERROR:
    case UPDATE_ERROR:
    case RESET_PASSWORD_ERROR:
      return {
        ...state,
        error: true,
        success: false,
        isLoading: false,
        message: payload.message,
      };

    case UPDATE_FIELD:
      const [key1, key2, key3] = payload.key.split('.');
      const memberId = state.result;
      const member = state.entities.members[memberId];

      return {
        ...state,
        entities: {
          ...state.entities,
          members: {
            ...state.entities.members,
            [memberId]: {
              ...member,
              [key1]: key2
                ? key3
                  ? {
                      ...member[key1],
                      [key2]: {
                        ...member[key1][key2],
                        [key3]: payload.value,
                      },
                    }
                  : {
                      ...member[key1],
                      [key2]: payload.value,
                    }
                : payload.value,
            },
          },
        },
      };

    case RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        error: false,
        success: true,
        isLoading: false,
        message: payload.message,
      };

    case CANCEL_SUBSCRIPTION_ERROR:
      return {
        ...state,
        error: true,
        success: false,
        isLoading: false,
        message: 'Cancellation failed. Please try again',
      };

    case MIGRATE_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        error: false,
        success: true,
        isLoading: false,
        message: '',
        entities: {
          ...state.entities,
          subscriptions: {
            ...state.entities.subscriptions,
            [payload.id]: {
              ...state.entities.subscriptions[payload.id],
              ...payload,
            },
          },
        },
      };

    case MIGRATE_SUBSCRIPTION_ERROR:
      return {
        ...state,
        error: true,
        success: false,
        isLoading: false,
        message:
          'An error occurred changing your subscription. Reload the page and please try again',
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/member.js